import { createFileRoute } from '@tanstack/react-router';
import { doNothing } from 'remeda';
import { z } from 'zod';

import { client } from '@@api/client';
import { CONTENT_LANGUAGE_HEADER } from '@@api/constants/headers';
import { unitySearchParams } from '@@containers/Search/unitySearchParams';
import { getContentLocale } from '@@scripts/store/settings/useSettingsStore';

import { SitemapTenant } from '../-components/SitemapTenant';
import { sitemapTreeQuery } from '../-components/sitemapTreeQuery';

export const Route = createFileRoute('/_authenticated/sitemaps/$tenantId')({
    validateSearch: unitySearchParams.pick({ q: true }).extend({
        menuId: z.number().optional().catch(undefined),
        isEditingMenuNode: z.boolean().optional().catch(undefined),
    }),
    // A new search makes a new match, which runs the loader again
    loaderDeps: ({ search }) => ({ menuId: search.menuId, q: search.q }),
    beforeLoad: ({ params, search }) => {
        const tenantId = Number(params.tenantId);
        const contentLocale = getContentLocale();
        const headers = { [CONTENT_LANGUAGE_HEADER]: contentLocale };

        // Built on every load, so the options follow the content language as well as the search
        return {
            sitemapTreeQuery: sitemapTreeQuery(tenantId, search.q, contentLocale),
            menusQuery: client.tenant.menu.getAll.queryOptions({ query: { tenantId }, headers }),
            menuQuery: client.tenant.menu.get.queryOptions(
                { params: { id: search.menuId ?? 0 }, headers },
                { refetchOnWindowFocus: false, enabled: search.menuId !== undefined },
            ),
        };
    },
    loader: ({ context, deps }) => {
        // Opening a menu or a menu node runs this loader again, and a new tree would replace the
        // unsaved changes in its list. A search is a new query, and the list refreshes the tree.
        context.queryClient
            .query({ ...context.sitemapTreeQuery, staleTime: 'static' })
            .catch(doNothing());

        // The menus are a panel of the page with their own loading state, and their errors
        // shouldn't replace the page, so the page doesn't wait for them. A search of the tree runs
        // this loader again, so it only loads menus it doesn't have, and the panel refreshes them.
        context.queryClient
            .query({ ...context.menusQuery, staleTime: 'static' })
            .catch(doNothing());

        if (deps.menuId !== undefined) {
            // Each fetch gives the menu nodes new ids, which the open node and the form refer to, so
            // a reload of the same page keeps the cached menu. Saving refetches it.
            context.queryClient
                .query({ ...context.menuQuery, staleTime: 'static' })
                .catch(doNothing());
        }
    },
    component: SitemapTenant,
});
